import React from "react";
import { useStaticQuery, Link as GatsbyLink, graphql } from "gatsby";
import styled from "styled-components";
import Box from "react-raster";
import Container from "../Container";
import { Copy } from "../Typography";
import wordmarkWhite from "./wordmarkWhite.png";

const Wrapper = styled.footer`
  background-color: ${({ theme }) => theme.colours.blue};
`;

const Wordmark = styled.img`
  margin-bottom: 40px;
`;

const Link = styled(GatsbyLink)`
  display: block;
  width: 100%;
  color: ${({ theme }) => theme.colours.white};
  font-size: 18px;
  line-height: 21px;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colours.green};
    opacity: 1;
  }
  margin-bottom: ${({ header }) => (header ? "40px" : "20px")};
  opacity: ${({ header }) => (header ? 1 : 0.7)};
`;

const ExternalLink = styled.a`
  display: block;
  width: 100%;
  color: ${({ theme }) => theme.colours.white};
  font-size: 18px;
  line-height: 21px;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colours.green};
    opacity: 1;
  }
  margin-bottom: ${({ header }) => (header ? "40px" : "20px")};
  opacity: ${({ header }) => (header ? 1 : 0.7)};
`;

const Header = styled(Copy)`
  font-size: 18px;
  line-height: 21px;
  margin-bottom: 40px;
`;

const SocialLink = styled.a`
  display: inline-block;
  margin-right: 20px;
  color: ${({ theme }) => theme.colours.white};
  font-size: 14px;
  line-height: 18px;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colours.green};
    opacity: 1;
  }
  &:last-child {
    margin-right: 0;
  }
`;

function ColumnHeader({ link }) {
  if (link.url) {
    if (link.url.includes("http")) {
      return (
        <ExternalLink href={link.url} target="_blank">
          {link.label}
        </ExternalLink>
      );
    } else {
      return (
        <Link
          header="true"
          to={link.url || "/"}
          title={link.hoverText}
          rel={link.url}
        >
          {link.label}
        </Link>
      );
    }
  }
  return (
    <Header isMarkdown={false} colour="white">
      {link.label}
    </Header>
  );
}

function Footer() {
  const data = useStaticQuery(graphql`
    {
      contentfulFooter {
        description {
          childMarkdownRemark {
            html
          }
        }
        columnOneLinks {
          url
          id
          label
          rel
          hoverText
        }
        columnTwoLinks {
          url
          id
          label
          rel
          hoverText
        }
        columnThreeLinks {
          url
          id
          label
          rel
          hoverText
        }
        legal {
          childMarkdownRemark {
            html
          }
        }
        socialLinks {
          url
          id
          label
          rel
          hoverText
        }
      }
    }
  `);

  const {
    description,
    columnOneLinks,
    columnTwoLinks,
    columnThreeLinks,
    legal,
    socialLinks,
  } = data?.contentfulFooter;

  return (
    <Wrapper>
      <Container>
        <Box cols={[6, 8, 4]} marginTop={1} marginRight={[0, 0, 1]}>
          <GatsbyLink to="/">
            <Wordmark
              width="280px"
              height="121px"
              src={wordmarkWhite}
              alt="AssemblyHQ Inc."
            />
          </GatsbyLink>
          <Copy style={{ fontSize: "18px" }} colour="white">
            {description}
          </Copy>
        </Box>
        {columnOneLinks && (
          <Box cols={[6, 12, 2]} marginTop={[1, 1, 2]}>
            <ColumnHeader link={columnOneLinks[0]} />
            {columnOneLinks.slice(1).map((link) =>
              link.url.includes("http") ? (
                <ExternalLink href={link.url} key={link.id} target="_blank">
                  {link.label}
                </ExternalLink>
              ) : (
                <Link
                  key={link.id}
                  to={link.url}
                  title={link.hoverText}
                  rel={link.url}
                >
                  {link.label}
                </Link>
              )
            )}
          </Box>
        )}
        {columnTwoLinks && (
          <Box cols={[6, 12, 2]} marginTop={[1, 1, 2]}>
            <ColumnHeader link={columnTwoLinks[0]} />
            {columnTwoLinks.slice(1).map((link) =>
              link.url.includes("http") ? (
                <ExternalLink href={link.url} key={link.id} target="_blank">
                  {link.label}
                </ExternalLink>
              ) : (
                <Link
                  key={link.id}
                  to={link.url}
                  title={link.hoverText}
                  rel={link.url}
                >
                  {link.label}
                </Link>
              )
            )}
            <ExternalLink
              href="#"
              className="termly-display-preferences"
              onClick={(e) => {
                e.preventDefault();
                window.displayPreferenceModal();
              }}
            >
              Consent Preferences
            </ExternalLink>
          </Box>
        )}
        {columnThreeLinks && (
          <Box cols={[6, 12, 2]} marginTop={[1, 1, 2]}>
            <ColumnHeader link={columnThreeLinks[0]} />
            {columnThreeLinks.slice(1).map((link) =>
              link.url.includes("http") ? (
                <ExternalLink href={link.url} key={link.id} target="_blank">
                  {link.label}
                </ExternalLink>
              ) : (
                <Link
                  key={link.id}
                  to={link.url}
                  title={link.hover}
                  rel={link.url}
                >
                  {link.label}
                </Link>
              )
            )}
          </Box>
        )}
      </Container>
      <Container as="div" paddingBottom="65px" hasBottomMargin={false}>
        <Box cols={[6, 12, 6]}>
          <Copy small colour="white">
            {legal}
          </Copy>
        </Box>
        {socialLinks && socialLinks.length > 0 && (
          <Box
            justifyContent={["left", "left", "right"]}
            cols={[6, 12, 6]}
            marginTop={[0.2, 0.2, 0]}
          >
            {socialLinks.map((link) => (
              <SocialLink key={link.id} href={link.url}>
                {link.label}
              </SocialLink>
            ))}
          </Box>
        )}
      </Container>
    </Wrapper>
  );
}

export default Footer;
